import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { toast } from "sonner";

import { listLabelTemplates, saveLabelTemplate } from "@/api/system_settings";
import { Button } from "@/components/ui/button";
import { apiErrorText } from "@/lib/scan-ui/screen";

/**
 * Макеты этикеток (сырой EZPL с плейсхолдерами).
 *
 * Правится здесь, а не в коде: макет верстают в GoLabel и меняют часто, а
 * выкатывать релиз ради переноса поля на два миллиметра склад не должен.
 * Пустой макет не принимаем — «печатать нечего» лучше поймать тут, чем на
 * складе у молчащего принтера.
 */
export function LabelTemplatesCard(): React.ReactElement {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["label-templates"], queryFn: listLabelTemplates });
  const [drafts, setDrafts] = React.useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: ({ code, template }: { code: string; template: string }) =>
      saveLabelTemplate(code, template),
    onSuccess: (tpl) => {
      toast.success(`Макет «${tpl.name}» сохранён`);
      setDrafts((d) => {
        const next = { ...d };
        delete next[tpl.code];
        return next;
      });
      void qc.invalidateQueries({ queryKey: ["label-templates"] });
    },
    onError: (e) => toast.error(apiErrorText(e)),
  });

  if (q.isLoading) return <p className="text-sm text-muted-foreground">Загрузка макетов…</p>;

  return (
    <div className="space-y-4">
      {q.data?.map((tpl) => {
        const value = drafts[tpl.code] ?? tpl.template;
        const dirty = value !== tpl.template;
        return (
          <div key={tpl.code} className="rounded-lg border bg-white p-4">
            <div className="mb-2 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold">{tpl.name}</h3>
                <p className="text-xs text-muted-foreground">
                  код <code>{tpl.code}</code> · выгрузка из GoLabel вставляется как есть
                </p>
              </div>
              <Button
                size="sm"
                disabled={!dirty || save.isPending}
                onClick={() => save.mutate({ code: tpl.code, template: value })}
              >
                Сохранить
              </Button>
            </div>

            {Object.keys(tpl.placeholders).length > 0 && (
              <details className="mb-2 rounded bg-slate-50 p-2 text-xs">
                <summary className="cursor-pointer text-slate-600">
                  Плейсхолдеры ({Object.keys(tpl.placeholders).length})
                </summary>
                <ul className="mt-2 space-y-1">
                  {Object.entries(tpl.placeholders).map(([key, hint]) => (
                    <li key={key}>
                      <code className="rounded bg-white px-1">{`{${key}}`}</code>{" "}
                      <span className="text-slate-500">— {hint}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-slate-500">
                  Длинный текст режется по строкам:{" "}
                  <code className="rounded bg-white px-1">{"{наименование:44:2}"}</code> —
                  вторая строка при ширине 44 символа.
                </p>
              </details>
            )}

            {/* Шпаргалка по шрифтам — здесь, а не в вики: макет правят в этом
                поле, и знание «как сделать жирным» нужно ровно тут. Сверено с
                EZPL Programmer's Manual (GoDEX, ревизия J). */}
            <details className="mb-2 rounded bg-slate-50 p-2 text-xs">
              <summary className="cursor-pointer text-slate-600">Шрифты EZPL</summary>
              <p className="mt-2 text-slate-600">
                <code className="rounded bg-white px-1">
                  At,x,y,x_mul,y_mul,gap,поворот,текст
                </code>{" "}
                — встроенные растровые: A 6пт, B 8, C 10, D 12, E 14, F 18, G 24,
                H 30. Размер меняется только умножением (до ×8, лесенкой),
                жирного начертания нет.
              </p>
              <p className="mt-2 text-slate-600">
                <code className="rounded bg-white px-1">
                  AT,x,y,ширина,высота,gap,стиль,0,0,текст
                </code>{" "}
                — встроенный TrueType: ширина и высота ЛЮБЫЕ (8…2000 точек), а в
                поле «стиль» к повороту 0–3 дописываются{" "}
                <code className="rounded bg-white px-1">B</code> жирный,{" "}
                <code className="rounded bg-white px-1">T</code> курсив,{" "}
                <code className="rounded bg-white px-1">U</code> подчёркивание —
                можно вместе: <code className="rounded bg-white px-1">0BU</code>.
              </p>
              <p className="mt-2 text-slate-600">
                300 dpi: 1 мм = 12 точек. Поворот: 0 = 0°, 1 = 90°, 2 = 180°,
                3 = 270° — у ВСЕХ объектов макета он должен быть одинаковым,
                иначе часть текста ляжет поперёк.
              </p>
            </details>

            <textarea
              className="h-72 w-full rounded border p-2 font-mono text-xs"
              spellCheck={false}
              value={value}
              onChange={(e) => setDrafts((d) => ({ ...d, [tpl.code]: e.target.value }))}
            />
            {dirty && (
              <p className="mt-1 text-xs text-amber-700">Есть несохранённые изменения.</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
